import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    Package, Plus, Pencil, Trash2, History, Tag, Layers, Truck, ArrowRightLeft,
    Wand2, Copy, AlertTriangle, Download, Upload, FileSpreadsheet, CheckCircle2, XCircle, Lock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const empty = {
    codice: "", descrizione: "", descrizione_etichetta: "", note: "", note_articolo: "", unita_misura: "PZ",
    imballo: "cartoni",
    cliente_nome: "", colore_nome: "",
    larghezza_principale: "", larghezza_soffietto: "", lunghezza: "", spessore_my: "",
    buste_per_rotolo: "", rotoli_per_cartone: "",
    piste_default: "",
    fascetta_id: "", cartone_id: "", tipo_pedana_id: "",
    macchine_compatibili: [],
    cartoni_per_pedana_default: "",
};

const SUFFISSI = ["R","S","T","U","V","X","Y","Z"];

function lookupCliente(nome, clienti) {
    if (!nome) return null;
    const n = nome.trim().toLowerCase();
    return clienti.find(c => (c.nome || "").toLowerCase() === n) || null;
}
function lookupColore(nome, colori) {
    if (!nome) return null;
    const n = nome.trim().toLowerCase();
    return colori.find(c => (c.nome || "").toLowerCase() === n) || null;
}

function predictClienteCodice(nome, clienti) {
    const found = lookupCliente(nome, clienti);
    if (found) return found.codice_breve;
    const up = (nome || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!up) return "";
    const taken = new Set(clienti.map(c => (c.codice_breve || "").toUpperCase()));
    for (const size of [2, 3, 4]) { const c = up.slice(0, size); if (!taken.has(c)) return c; }
    for (let i = 1; i < 99; i++) { const c = up.slice(0, 2) + i; if (!taken.has(c)) return c; }
    return up.slice(0, 2);
}
function predictColoreCodice(nome, colori) {
    const found = lookupColore(nome, colori);
    if (found) return found.codice_breve;
    const up = (nome || "").toUpperCase().replace(/[^A-Z]/g, "");
    if (!up) return "";
    const taken = new Set(colori.map(c => (c.codice_breve || "").toUpperCase()));
    for (const size of [1, 2, 3]) { const c = up.slice(0, size); if (!taken.has(c)) return c; }
    for (let i = 1; i < 99; i++) { const c = up[0] + i; if (!taken.has(c)) return c; }
    return up[0];
}
function nextNumericCodice(coll) {
    let mx = 0;
    for (const x of coll) { const n = parseInt(x.codice_breve); if (!isNaN(n) && n > mx) mx = n; }
    return String(mx + 1);
}

function suggestedCode(f, clienti, colori, misure, confez, allArticles, editId, misGroupCode) {
    const cli = predictClienteCodice(f.cliente_nome, clienti);
    const col = predictColoreCodice(f.colore_nome, colori);
    const w = (parseInt(f.larghezza_principale)||0) + (parseInt(f.larghezza_soffietto)||0);
    const l = parseInt(f.lunghezza)||0;
    let mis = "";
    if (w && l) {
        // Priorità: gruppo scelto dall'utente (tendina) → gruppo suggerito dal backend (misGroupCode) → match esatto → next
        if (f.misura_group_hint && f.misura_group_hint !== "__new__") mis = f.misura_group_hint;
        else if (misGroupCode && f.misura_group_hint !== "__new__") mis = misGroupCode;
        else {
            const found = misure.find(x => x.larghezza === w && x.lunghezza === l);
            mis = found ? found.codice_breve : nextNumericCodice(misure);
        }
    }
    const bpr = parseInt(f.buste_per_rotolo)||0;
    const rpc = parseInt(f.rotoli_per_cartone)||0;
    let cfz = "";
    if (bpr && rpc) {
        const found = confez.find(x => x.buste_per_rotolo === bpr && x.rotoli_per_cartone === rpc);
        cfz = found ? found.codice_breve : nextNumericCodice(confez);
    }
    if (!(cli && col && mis && cfz)) return "";
    const base = (cli + mis + col + cfz).toUpperCase();
    for (const s of SUFFISSI) {
        const cand = (base + s).toUpperCase();
        const clash = allArticles.find(a => a.codice.toUpperCase() === cand && a.id !== editId);
        if (!clash) return cand;
    }
    return base + "R";
}
function totale(f) { return (parseInt(f.larghezza_principale)||0) + (parseInt(f.larghezza_soffietto)||0); }
function pisteMax(f) { const t = totale(f); return t > 0 ? Math.floor(181 / t) : 0; }
function pesoBusta(f) {
    const l = parseInt(f.lunghezza)||0, w = totale(f), s = parseInt(f.spessore_my)||0;
    return (l && w && s) ? +(l * w * s * 0.000092 * 2).toFixed(2) : 0;
}
function pesoRotolo(f) { const bpr = parseInt(f.buste_per_rotolo)||0; return +(pesoBusta(f) * bpr).toFixed(2); }
function pesoCartone(f) { const rpc = parseInt(f.rotoli_per_cartone)||0; return +(pesoRotolo(f) * rpc / 1000).toFixed(3); }

const EVENT_META = {
    lotto: { label: "Lotto operatore", icon: Tag, tone: "emerald" },
    pedana: { label: "Su pedana", icon: Layers, tone: "amber" },
    prod_to_mag: { label: "Prod → Mag", icon: ArrowRightLeft, tone: "amber" },
    mag_internal: { label: "Sposta pedana", icon: ArrowRightLeft, tone: "blue" },
    uscita: { label: "Uscita", icon: Truck, tone: "crimson" },
};
const TONES = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    blue: "bg-blue-500/15 text-blue-300 border-blue-500/40",
    crimson: "bg-red-500/15 text-red-300 border-red-500/40",
};

export default function Articoli() {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "ufficio";
    const [items, setItems] = useState([]);
    const [fascette, setFascette] = useState([]);
    const [cartoni, setCartoni] = useState([]);
    const [tipiPed, setTipiPed] = useState([]);
    const [clienti, setClienti] = useState([]);
    const [colori, setColori] = useState([]);
    const [misure, setMisure] = useState([]);
    const [confez, setConfez] = useState([]);
    const [impianti, setImpianti] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);
    const [origColore, setOrigColore] = useState(null);
    const [codiceTouched, setCodiceTouched] = useState(false);
    const [descTouched, setDescTouched] = useState(false);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [histOpen, setHistOpen] = useState(false);
    const [hist, setHist] = useState(null);
    // Import Excel
    const fileInputRef = useRef(null);
    const [importOpen, setImportOpen] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [importCommitting, setImportCommitting] = useState(false);
    const [clientCodeOverrides, setClientCodeOverrides] = useState({});   // nome_lower -> code
    const [misuraAssignments, setMisuraAssignments] = useState({});       // row_num -> codice_breve|"__new__"
    const [confAssignments, setConfAssignments] = useState({});           // row_num -> codice_breve|"__new__"

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/articles/import/template", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = "articoli-modello.xlsx"; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const onFilePicked = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setImportFile(f); setImportPreview(null); setImportOpen(true); setImportLoading(true);
        setClientCodeOverrides({});
        setMisuraAssignments({});
        setConfAssignments({});
        try {
            const fd = new FormData(); fd.append("file", f);
            const { data } = await api.post("/articles/import/preview", fd);
            setImportPreview(data);
            // Pre-fill overrides con i proposed_code
            const map = {};
            for (const c of (data.new_clients || [])) map[c.nome.toLowerCase()] = c.proposed_code;
            setClientCodeOverrides(map);
        } catch (err) { toast.error(formatApiError(err)); setImportOpen(false); }
        finally { setImportLoading(false); }
    };

    const doImportCommit = async () => {
        if (!importFile) return;
        setImportCommitting(true);
        try {
            const fd = new FormData();
            fd.append("file", importFile);
            fd.append("client_codes", JSON.stringify(clientCodeOverrides));
            fd.append("misura_assignments", JSON.stringify(misuraAssignments));
            fd.append("confezionamento_assignments", JSON.stringify(confAssignments));
            const { data } = await api.post("/articles/import/commit", fd);
            toast.success(`Import: ${data.created} creati · ${data.duplicates} duplicati · ${data.errors} errori`);
            setImportOpen(false); setImportFile(null); setImportPreview(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setImportCommitting(false); }
    };

    const load = async () => {
        setLoading(true);
        try {
            const [a, f, c, t, cli, col, mis, cfz, imp] = await Promise.all([
                api.get("/articles"), api.get("/fascette/"),
                api.get("/cartoni/"), api.get("/tipi-pedana/"),
                api.get("/clienti/"), api.get("/colori/"),
                api.get("/misure/"), api.get("/confezionamenti/"),
                api.get("/impianti/"),
            ]);
            setItems(a.data); setFascette(f.data); setCartoni(c.data); setTipiPed(t.data);
            setClienti(cli.data); setColori(col.data); setMisure(mis.data); setConfez(cfz.data);
            setImpianti(imp.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditId(null); setOrigColore(null); setForm(empty); setCodiceTouched(false); setDescTouched(false); setOpen(true); };
    const openDup = (a) => {
        setEditId(null); setOrigColore(null);
        setForm({
            ...empty,
            descrizione: a.descrizione, descrizione_etichetta: a.descrizione_etichetta || "",
            cliente_nome: a.cliente_nome || "",
            colore_nome: "", // duplica → cambia colore
            larghezza_principale: a.larghezza_principale ?? "", larghezza_soffietto: a.larghezza_soffietto ?? "",
            lunghezza: a.lunghezza ?? "", spessore_my: a.spessore_my ?? "",
            buste_per_rotolo: a.buste_per_rotolo ?? "", rotoli_per_cartone: a.rotoli_per_cartone ?? "",
            piste_default: a.piste_default ?? "",
            fascetta_id: a.fascetta_id || "", cartone_id: a.cartone_id || "", tipo_pedana_id: a.tipo_pedana_id || "",
            cartoni_per_pedana_default: a.cartoni_per_pedana_default ?? "",
            imballo: a.imballo || "cartoni",
            note_articolo: a.note_articolo || "",
            codice: "",
            unita_misura: a.unita_misura || "PZ",
        });
        setCodiceTouched(false); setDescTouched(false);
        setOpen(true);
    };
    const openEdit = (a) => {
        setEditId(a.id); setOrigColore(a.colore_nome || null);
        setCodiceTouched(true); setDescTouched(true);
        setForm({
            codice: a.codice, descrizione: a.descrizione, descrizione_etichetta: a.descrizione_etichetta || "",
            note: a.note || "", note_articolo: a.note_articolo || "", unita_misura: a.unita_misura || "PZ",
            imballo: a.imballo || "cartoni",
            cliente_nome: a.cliente_nome || "",
            colore_nome: a.colore_nome || "",
            larghezza_principale: a.larghezza_principale ?? "", larghezza_soffietto: a.larghezza_soffietto ?? "",
            lunghezza: a.lunghezza ?? "", spessore_my: a.spessore_my ?? "",
            buste_per_rotolo: a.buste_per_rotolo ?? "", rotoli_per_cartone: a.rotoli_per_cartone ?? "",
            piste_default: a.piste_default ?? "",
            fascetta_id: a.fascetta_id || "", cartone_id: a.cartone_id || "", tipo_pedana_id: a.tipo_pedana_id || "",
            cartoni_per_pedana_default: a.cartoni_per_pedana_default ?? "",
            macchine_compatibili: a.macchine_compatibili || [],
        });
        setOpen(true);
    };
    const openStorico = async (a) => {
        setHistOpen(true); setHist(null);
        try { const { data } = await api.get(`/articles/${a.id}/storico`); setHist(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // lookup live per warning
    const cliFound = lookupCliente(form.cliente_nome, clienti);
    const colFound = lookupColore(form.colore_nome, colori);
    const wTot = (parseInt(form.larghezza_principale)||0) + (parseInt(form.larghezza_soffietto)||0);
    const lLen = parseInt(form.lunghezza)||0;
    const misuraFound = wTot && lLen ? misure.find(x => x.larghezza === wTot && x.lunghezza === lLen) : null;
    const bpr = parseInt(form.buste_per_rotolo)||0;
    const rpc = parseInt(form.rotoli_per_cartone)||0;
    const confezFound = bpr && rpc ? confez.find(x => x.buste_per_rotolo === bpr && x.rotoli_per_cartone === rpc) : null;

    // Lookup gruppo misura con cascata fascetta-first + tolleranza ±3/±3 (form manuale).
    // Debounce 300ms sui campi larghezza/lunghezza/fascetta.
    const [misGroup, setMisGroup] = useState(null);
    useEffect(() => {
        if (!wTot || !lLen) { setMisGroup(null); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const params = { larghezza_totale: wTot, lunghezza: lLen };
                if (form.fascetta_id) params.fascetta_id = form.fascetta_id;
                const { data } = await api.get("/articles/misura-lookup", { params });
                if (!cancelled) setMisGroup(data);
            } catch {
                if (!cancelled) setMisGroup(null);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [wTot, lLen, form.fascetta_id]);

    const sugg = suggestedCode(form, clienti, colori, misure, confez, items, editId, misGroup?.group_code);

    // Descrizione composta incrementalmente man mano che compili i campi.
    // Priorità dimensioni: fascetta (larg/alt dichiarata) → preferita del gruppo misura → misure effettive.
    const fascettaSel = fascette.find(f => f.id === form.fascetta_id);
    const wActual = (parseInt(form.larghezza_principale)||0) + (parseInt(form.larghezza_soffietto)||0);
    const lActual = parseInt(form.lunghezza)||0;
    const wTotDesc = (fascettaSel?.larg_dichiarata_cm ?? null)
        ?? (misGroup?.group_preferita?.larghezza ?? null)
        ?? wActual;
    const lLenDesc = (fascettaSel?.alt_dichiarata_cm ?? null)
        ?? (misGroup?.group_preferita?.lunghezza ?? null)
        ?? lActual;
    const descParts = [];
    if (form.colore_nome) {
        // Usa il plurale (lowercase) dall'anagrafica colori; fallback sul singolare digitato
        const col = colori.find(c => (c.nome || "").toLowerCase() === form.colore_nome.trim().toLowerCase());
        const plur = (col?.nome_plurale || form.colore_nome).toLowerCase();
        descParts.push(`Sacchi ${plur}`);
    }
    if (wTotDesc && lLenDesc) descParts.push(`mis. ${wTotDesc}x${lLenDesc}`);
    else if (wTotDesc) descParts.push(`larg. ${wTotDesc}`);
    else if (lLenDesc) descParts.push(`lung. ${lLenDesc}`);
    if (fascettaSel) descParts.push(`- fascetta ${fascettaSel.descrizione || fascettaSel.codice}`);
    const descDefault = descParts.join(" ").replace(" - ", " - ");

    // Valori mostrati (derivati): finché l'utente non tocca, mostrano suggerimento live
    const codiceShown = codiceTouched ? form.codice : (sugg || "");
    const descrizioneShown = descTouched ? form.descrizione : (descDefault || "");

    const codiceDiff = sugg && sugg !== (form.codice || "").toUpperCase();
    const coloreCambiato = editId && origColore && form.colore_nome && form.colore_nome.toLowerCase() !== (origColore||"").toLowerCase();

    // Validazione: solo i dati fisici sono obbligatori (cliente/colore/misura/confez vengono auto-creati)
    const errors = [];
    if (!form.cliente_nome?.trim()) errors.push("Cliente obbligatorio");
    if (!form.colore_nome?.trim()) errors.push("Colore obbligatorio");
    if (!(descrizioneShown || "").trim()) errors.push("Compila colore e misure per generare la descrizione");
    if (!(parseInt(form.larghezza_principale) > 0)) errors.push("Larghezza principale obbligatoria");
    if (!(parseInt(form.lunghezza) > 0)) errors.push("Lunghezza obbligatoria");
    if (!(parseInt(form.spessore_my) > 0)) errors.push("Spessore obbligatorio");
    if (!(parseInt(form.buste_per_rotolo) > 0)) errors.push("Buste per rotolo obbligatorio");
    if (!(parseInt(form.rotoli_per_cartone) > 0)) errors.push("Rotoli per cartone obbligatorio");
    if (!form.piste_default) errors.push("Piste standard obbligatorie");
    const canSave = errors.length === 0;

    const save = async () => {
        if (!canSave) { toast.error(errors[0]); return; }
        if (coloreCambiato) {
            const ok = window.confirm(
                "ATTENZIONE: stai cambiando il COLORE su un articolo esistente. Sballa il magazzino.\n" +
                "Meglio duplicare l'articolo. Vuoi procedere comunque?"
            );
            if (!ok) return;
        }
        try {
            const payload = { ...form };
            // Se l'utente non ha toccato codice/descrizione, usa i valori suggeriti live
            if (!codiceTouched && sugg) payload.codice = sugg;
            if (!descTouched && descDefault) payload.descrizione = descDefault;
            else if (!descTouched) payload.descrizione = descrizioneShown;
            const numFields = ["larghezza_principale","larghezza_soffietto","lunghezza","spessore_my",
                                "buste_per_rotolo","rotoli_per_cartone","piste_default","cartoni_per_pedana_default"];
            for (const k of numFields) payload[k] = payload[k] === "" ? null : parseInt(payload[k]);
            for (const k of ["fascetta_id","cartone_id","tipo_pedana_id"]) {
                if (payload[k] === "") payload[k] = null;
            }
            for (const k of ["cliente_nome","colore_nome"]) {
                if (payload[k] === "") payload[k] = null;
            }
            payload.pezzi_per_cartone = (parseInt(payload.buste_per_rotolo)||0) * (parseInt(payload.rotoli_per_cartone)||0) || 1;
            if (editId) { await api.put(`/articles/${editId}`, payload); toast.success("Articolo aggiornato"); }
            else { await api.post("/articles", payload); toast.success("Articolo creato"); }
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const del = async (a) => {
        if (!window.confirm(`Eliminare articolo ${a.codice}?`)) return;
        try { await api.delete(`/articles/${a.id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const filtered = useMemo(() => items.filter(i =>
        !q || i.codice.toLowerCase().includes(q.toLowerCase()) ||
        i.descrizione.toLowerCase().includes(q.toLowerCase())
    ), [items, q]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-amber-500/80">ANAGRAFICA</div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Articoli</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-3xl">
                        Gestione dell'anagrafica delle buste per rifiuti.<br/>
                        Il codice articolo viene generato automaticamente in base a cliente, misura, colore e confezionamento.<br/>
                        Il peso dell'articolo viene calcolato automaticamente.
                    </p>
                </div>
                {canEdit && (
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={downloadTemplate} variant="outline" data-testid="button-download-template"
                                className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 gap-2">
                            <Download size={14} /> Scarica modello Excel
                        </Button>
                        <Button onClick={()=>fileInputRef.current?.click()} variant="outline" data-testid="button-import-excel"
                                className="bg-transparent border-emerald-600/50 text-emerald-300 hover:bg-emerald-500/10 gap-2">
                            <Upload size={14} /> Importa da Excel
                        </Button>
                        <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden"
                               onChange={onFilePicked} data-testid="input-import-file" />
                        <Button onClick={openNew} data-testid="button-new-article" className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                            <Plus size={16} /> Nuovo articolo
                        </Button>
                    </div>
                )}
            </div>

            <Card className="card p-4">
                <Input data-testid="input-search-articles" value={q} onChange={e=>setQ(e.target.value)}
                       placeholder="Cerca per codice o descrizione…"
                       className="bg-slate-950/40 border-slate-800 max-w-md" />
            </Card>

            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">Descrizione</th>
                                <th className="text-right px-5 py-3">Larg.</th>
                                <th className="text-right px-5 py-3">Lung.</th>
                                <th className="text-right px-5 py-3">Sp.</th>
                                <th className="text-right px-5 py-3">Rot/Cart × Buste/Rot</th>
                                <th className="text-right px-5 py-3">Peso rot.</th>
                                <th className="text-right px-5 py-3">Piste</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-articoli">
                            {loading && <tr><td colSpan={9} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-500">
                                    <Package size={36} className="mx-auto mb-3 opacity-40" />
                                    <div className="text-sm">Nessun articolo</div>
                                </td></tr>
                            )}
                            {filtered.map(a => (
                                <tr key={a.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`row-article-${a.codice}`}>
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{a.codice}</td>
                                    <td className="px-5 py-3 text-slate-100">
                                        {a.descrizione}
                                        {a.descrizione_etichetta && a.descrizione_etichetta !== a.descrizione &&
                                            <div className="text-[10px] text-slate-500">🏷 {a.descrizione_etichetta}</div>}
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono text-xs">
                                        {a.larghezza_principale ?? "—"}
                                        {a.larghezza_soffietto ? <span>+{a.larghezza_soffietto}</span> : null}
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono text-xs">{a.lunghezza ?? "—"}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs">{a.spessore_my ? `${a.spessore_my}my` : "—"}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-400">
                                        {a.rotoli_per_cartone || "—"}×{a.buste_per_rotolo || "—"}
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-emerald-300">{a.peso_rotolo_g ? `${a.peso_rotolo_g}g` : "—"}</td>
                                    <td className="px-5 py-3 text-right">
                                        {a.piste_default ? <Badge className="badge-pill bg-blue-500/15 text-blue-300 border-blue-500/40">{a.piste_default}×</Badge> : "—"}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        <button onClick={()=>openStorico(a)} data-testid={`button-storico-${a.codice}`} title="Storico"
                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><History size={14} /></button>
                                        {canEdit && (<>
                                            <button onClick={()=>openDup(a)} data-testid={`button-dup-${a.codice}`} title="Duplica (cambia colore)"
                                                    className="p-2 rounded hover:bg-slate-700/50 text-violet-300"><Copy size={14} /></button>
                                            <button onClick={()=>openEdit(a)} data-testid={`button-edit-${a.codice}`}
                                                    className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                            <button onClick={()=>del(a)} data-testid={`button-delete-${a.codice}`}
                                                    className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                        </>)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Edit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>

                    <div className="space-y-6">
                        {/* Cliente & Colore (testo libero con lookup automatico da anagrafica) */}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Cliente · Colore</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs text-slate-400">Cliente *</Label>
                                    <Input data-testid="input-cliente-nome" value={form.cliente_nome}
                                           onChange={e=>setForm({...form, cliente_nome: e.target.value})}
                                           placeholder="SASCARO"
                                           list="clienti-list"
                                           className="bg-slate-950/50 border-slate-800 mt-1" />
                                    <datalist id="clienti-list">
                                        {clienti.map(c => <option key={c.id} value={c.nome} />)}
                                    </datalist>
                                    <div className="text-[10px] mt-1">
                                        {cliFound
                                            ? <span className="text-emerald-400">✓ codice: <span className="font-mono">{cliFound.codice_breve}</span></span>
                                            : form.cliente_nome ? <span className="text-amber-400">⚡ nuovo — codice creato al salvataggio</span> : <span className="text-slate-500">Digita il nome cliente</span>}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs text-slate-400">Colore *</Label>
                                    <Input data-testid="input-colore-nome" value={form.colore_nome}
                                           onChange={e=>setForm({...form, colore_nome: e.target.value})}
                                           placeholder="Nero"
                                           list="colori-list"
                                           className="bg-slate-950/50 border-slate-800 mt-1" />
                                    <datalist id="colori-list">
                                        {colori.map(c => <option key={c.id} value={c.nome} />)}
                                    </datalist>
                                    <div className="text-[10px] mt-1">
                                        {colFound
                                            ? <span className="text-emerald-400">✓ codice: <span className="font-mono">{colFound.codice_breve}</span></span>
                                            : form.colore_nome ? <span className="text-amber-400">⚡ nuovo — codice creato al salvataggio</span> : <span className="text-slate-500">Digita il nome colore</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-1 text-[10px] text-slate-500">Il suffisso finale (R/S/T/…) viene aggiunto automaticamente per evitare duplicati.</div>
                        </div>

                        {/* Codice */}
                        <div>
                            <Label className="text-xs text-slate-400">Codice articolo *</Label>
                            <div className="flex gap-2 mt-1">
                                <Input data-testid="input-codice" value={codiceShown}
                                       disabled={!!editId}
                                       onChange={e=>{ setCodiceTouched(true); setForm({...form, codice: e.target.value.toUpperCase()}); }}
                                       className={`bg-slate-950/50 border-slate-800 font-mono ${editId ? "opacity-70 cursor-not-allowed" : ""}`} />
                                {!editId && sugg && codiceDiff && (
                                    <Button type="button" variant="outline" onClick={()=>setForm({...form, codice: sugg})}
                                            data-testid="button-use-suggested" className="bg-amber-500/10 border-amber-500/40 text-amber-300 gap-2 whitespace-nowrap">
                                        <Wand2 size={12} /> Usa "{sugg}"
                                    </Button>
                                )}
                            </div>
                            {editId && (
                                <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-1" data-testid="codice-locked-hint">
                                    <Lock size={10} /> Codice bloccato dopo il primo salvataggio
                                </div>
                            )}
                            {coloreCambiato && (
                                <div className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2 flex items-start gap-2">
                                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                    <div>Cambiare il <b>colore</b> su un articolo esistente sballa il magazzino. Consigliato: <b>Duplica</b> e crea un nuovo articolo col colore diverso.</div>
                                </div>
                            )}
                        </div>

                        {/* Descrizioni */}
                        <div className="grid md:grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-400">Descrizione *</Label>
                                <Input data-testid="input-descrizione" value={descrizioneShown}
                                       onChange={e=>{ setDescTouched(true); setForm({...form, descrizione: e.target.value}); }}
                                       className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                            <div><Label className="text-xs text-slate-400">Descrizione etichetta</Label>
                                <Input data-testid="input-descrizione-etichetta" value={form.descrizione_etichetta}
                                       onChange={e=>setForm({...form, descrizione_etichetta: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                        </div>

                        {/* Misure */}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Misure busta</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div><Label className="text-xs text-slate-400">Larghezza principale (cm) *</Label>
                                    <Input data-testid="input-larg-principale" type="number" min="0" value={form.larghezza_principale}
                                           onChange={e=>setForm({...form, larghezza_principale: e.target.value})}
                                           className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                                <div><Label className="text-xs text-slate-400">Soffietto (cm)</Label>
                                    <Input data-testid="input-soffietto" type="number" min="0" value={form.larghezza_soffietto}
                                           onChange={e=>setForm({...form, larghezza_soffietto: e.target.value})}
                                           className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                                <div><Label className="text-xs text-slate-400">Lunghezza (cm) *</Label>
                                    <Input data-testid="input-lunghezza" type="number" min="0" value={form.lunghezza}
                                           onChange={e=>setForm({...form, lunghezza: e.target.value})}
                                           className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                                <div><Label className="text-xs text-slate-400">Spessore (my) *</Label>
                                    <Input data-testid="input-spessore" type="number" min="0" value={form.spessore_my}
                                           onChange={e=>setForm({...form, spessore_my: e.target.value})}
                                           className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                                <Badge className="badge-pill bg-slate-800 text-slate-300 border-slate-700">Larghezza tot: <b className="ml-1 font-mono">{totale(form) || "—"}</b> cm</Badge>
                                <Badge className="badge-pill bg-blue-500/15 text-blue-300 border-blue-500/40">Piste max: <b className="ml-1 font-mono">{pisteMax(form) || "—"}</b></Badge>
                                <Badge className="badge-pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Peso busta: <b className="ml-1 font-mono">{pesoBusta(form) || "—"}g</b></Badge>
                                <Badge className="badge-pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Peso rotolo: <b className="ml-1 font-mono">{pesoRotolo(form) || "—"}g</b></Badge>
                                <Badge className="badge-pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Peso {form.imballo === "vaschette" ? "vaschetta" : "cartone"}: <b className="ml-1 font-mono">{pesoCartone(form) || "—"}kg</b></Badge>
                                {!editId && wTot > 0 && lLen > 0 && (
                                    misGroup?.is_exact && misGroup?.group_code
                                        ? <Badge className="badge-pill bg-emerald-500/20 text-emerald-300 border-emerald-500/40" title={`Match esatto in anagrafica`}>Gruppo {misGroup.group_code} <span className="opacity-70 ml-1">esatto · mis. {misGroup.group_preferita?.larghezza}×{misGroup.group_preferita?.lunghezza}</span></Badge>
                                        : misGroup?.fascetta_priority && misGroup?.group_code
                                            ? <Badge className="badge-pill bg-sky-500/20 text-sky-300 border-sky-500/40" title="Scelto dalla fascetta">Gruppo {misGroup.group_code} · fascetta <span className="opacity-70 ml-1">mis. {misGroup.group_preferita?.larghezza}×{misGroup.group_preferita?.lunghezza}</span></Badge>
                                        : misGroup?.group_code && !misGroup?.is_new_group
                                            ? <Badge className="badge-pill bg-amber-500/20 text-amber-300 border-amber-500/40" title="Match entro tolleranza ±3/±3">→ Gruppo {misGroup.group_code} · tolleranza <span className="opacity-70 ml-1">mis. {misGroup.group_preferita?.larghezza}×{misGroup.group_preferita?.lunghezza}</span></Badge>
                                            : <Badge className="badge-pill bg-amber-600/30 text-amber-200 border-amber-600/50">Nuova misura {wTot}×{lLen} — codice auto al salvataggio</Badge>
                                )}
                                {!editId && wTot > 0 && lLen > 0 && (misGroup?.group_code || misGroup?.is_new_group) && (
                                    <select value={form.misura_group_hint ?? (misGroup?.is_new_group ? "__new__" : (misGroup?.group_code || ""))}
                                            onChange={e=>setForm({...form, misura_group_hint: e.target.value})}
                                            data-testid="select-misura-group-hint"
                                            className="ml-1 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-200 font-mono">
                                        {misGroup?.group_code && <option value={misGroup.group_code}>Gruppo {misGroup.group_code}{misGroup.group_preferita ? ` · pref. ${misGroup.group_preferita.larghezza}×${misGroup.group_preferita.lunghezza}` : ""}</option>}
                                        {(misGroup?.group_alternatives || []).filter(a => a.codice_breve !== misGroup?.group_code).map(a => (
                                            <option key={a.codice_breve} value={a.codice_breve}>Gruppo {a.codice_breve} · pref. {a.preferita_larghezza}×{a.preferita_lunghezza}</option>
                                        ))}
                                        <option value="__new__">— Crea nuovo gruppo —</option>
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Confezionamento + piste */}
                        <div className="grid md:grid-cols-3 gap-3">
                            <div><Label className="text-xs text-slate-400">Buste per rotolo *</Label>
                                <Input data-testid="input-buste-rotolo" type="number" min="1" value={form.buste_per_rotolo}
                                       onChange={e=>setForm({...form, buste_per_rotolo: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            <div><Label className="text-xs text-slate-400">Rotoli per {form.imballo === "vaschette" ? "vaschetta" : "cartone"} *</Label>
                                <Input data-testid="input-rotoli-cartone" type="number" min="1" value={form.rotoli_per_cartone}
                                       onChange={e=>setForm({...form, rotoli_per_cartone: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            <div><Label className="text-xs text-slate-400">Piste standard</Label>
                                <Select value={form.piste_default ? String(form.piste_default) : ""} onValueChange={v=>setForm({...form, piste_default: v})}>
                                    <SelectTrigger data-testid="select-piste" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                        {[1,2,3].map(n => <SelectItem key={n} value={String(n)} disabled={pisteMax(form) > 0 && n > pisteMax(form)}>{n} pist{n>1?"e":"a"}{pisteMax(form)>0 && n>pisteMax(form) ? " (rullo insuff.)" : ""}</SelectItem>)}
                                    </SelectContent>
                                </Select></div>
                        </div>
                        {!editId && bpr > 0 && rpc > 0 && !confezFound && (
                            <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                                ⚡ Nuovo confezionamento <b className="font-mono">{rpc}×{bpr}</b> — codice creato in automatico al salvataggio
                            </div>
                        )}
                        {!editId && bpr > 0 && rpc > 0 && confezFound && (
                            <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                                Codice confezionamento: <b className="font-mono">{confezFound.codice_breve}</b>
                            </div>
                        )}

                        {/* Anagrafiche */}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Riferimenti anagrafiche</div>
                            <div className="grid md:grid-cols-3 gap-3">
                                <div><Label className="text-xs text-slate-400">Fascetta</Label>
                                    <Select value={form.fascetta_id || undefined} onValueChange={v=>setForm({...form, fascetta_id: v})}>
                                        <SelectTrigger data-testid="select-fascetta" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                            {fascette.map(f => <SelectItem key={f.id} value={f.id}><span className="font-mono text-amber-300 mr-2">{f.codice}</span>{f.descrizione}</SelectItem>)}
                                        </SelectContent>
                                    </Select></div>
                                {form.imballo !== "vaschette" && (
                                <div><Label className="text-xs text-slate-400">Tipo cartone</Label>
                                    <Select value={form.cartone_id || undefined} onValueChange={v=>{
                                        const c = cartoni.find(x => x.id === v);
                                        const patch = { cartone_id: v };
                                        // Auto-fill "cartoni per pedana (default)" con la capacità del cartone
                                        // per il tipo pedana correntemente scelto — solo se non è stato digitato a mano.
                                        if (c && form.tipo_pedana_id && !form._cpp_manual) {
                                            const v2 = (c.cartoni_per_pedana_map || {})[form.tipo_pedana_id];
                                            if (v2) patch.cartoni_per_pedana_default = String(v2);
                                        }
                                        setForm({...form, ...patch});
                                    }}>
                                        <SelectTrigger data-testid="select-cartone" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                            {cartoni.map(c => <SelectItem key={c.id} value={c.id}>
                                                <span className="font-mono text-amber-300 mr-2">{c.codice}</span>{c.descrizione}
                                            </SelectItem>)}
                                        </SelectContent>
                                    </Select></div>
                                )}
                                <div><Label className="text-xs text-slate-400">Tipo pedana default</Label>
                                    <Select value={form.tipo_pedana_id || undefined} onValueChange={v=>{
                                        const patch = { tipo_pedana_id: v };
                                        // Se ho anche un cartone e non ho digitato a mano, aggiorno cartoni/pedana dalla matrice
                                        const c = cartoni.find(x => x.id === form.cartone_id);
                                        if (c && !form._cpp_manual) {
                                            const v2 = (c.cartoni_per_pedana_map || {})[v];
                                            if (v2) patch.cartoni_per_pedana_default = String(v2);
                                        }
                                        setForm({...form, ...patch});
                                    }}>
                                        <SelectTrigger data-testid="select-tipo-pedana" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                            {tipiPed.map(t => <SelectItem key={t.id} value={t.id}><span className="font-mono text-amber-300 mr-2">{t.codice}</span>{t.descrizione}</SelectItem>)}
                                        </SelectContent>
                                    </Select></div>                            </div>
                            <div className="mt-3">
                                <Label className="text-xs text-slate-400">Cartoni per pedana (default)</Label>
                                <Input data-testid="input-cartoni-pedana" type="number" min="1" value={form.cartoni_per_pedana_default}
                                       onChange={e=>setForm({...form, cartoni_per_pedana_default: e.target.value, _cpp_manual: true})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 max-w-xs font-mono" />
                                {form.cartone_id && form.tipo_pedana_id && !form._cpp_manual && (
                                    <div className="text-[10px] text-slate-500 mt-1">Pre-compilato dalla matrice cartone × tipo pedana in Anagrafiche → Cartoni — modificabile a mano.</div>
                                )}
                                {form.cartone_id && form.tipo_pedana_id && !form._cpp_manual && !form.cartoni_per_pedana_default && (
                                    <div className="text-[10px] text-amber-400 mt-1">
                                        <AlertTriangle size={10} className="inline mr-1" />
                                        Nessuna capacità definita per questa combinazione — aggiungila in Anagrafiche → Cartoni oppure inserisci il valore a mano qui sopra.
                                    </div>
                                )}
                            </div>
                            <div className="mt-3">
                                <Label className="text-xs text-slate-400">Imballo *</Label>
                                <div className="flex gap-2 mt-2" data-testid="imballo-group">
                                    {[
                                        {v:"cartoni", l:"Cartoni"},
                                        {v:"vaschette", l:"Vaschette"},
                                    ].map(op => {
                                        const checked = (form.imballo || "cartoni") === op.v;
                                        return (
                                            <button type="button" key={op.v}
                                                data-testid={`imballo-${op.v}`}
                                                onClick={()=>setForm({...form, imballo: op.v})}
                                                className={`px-4 py-2 rounded-md border text-xs font-semibold transition ${checked ? "bg-amber-500/15 border-amber-500/60 text-amber-200" : "bg-slate-800/50 border-slate-700 text-slate-400 hover:text-slate-100"}`}>
                                                {op.l}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1">Proposto sull'ordine — l'ufficio può cambiarlo per singolo ordine.</div>
                            </div>
                            <div className="mt-3">
                                <Label className="text-xs text-slate-400">Macchine compatibili * <span className="text-slate-600">(almeno una)</span></Label>
                                <div className="flex flex-wrap gap-2 mt-2" data-testid="macchine-compatibili">
                                    {impianti.length === 0 && <div className="text-xs text-slate-500">Nessuna macchina in anagrafica</div>}
                                    {impianti.map(i => {
                                        const checked = (form.macchine_compatibili || []).includes(i.id);
                                        return (
                                            <label key={i.id} data-testid={`macchina-chip-${i.codice}`}
                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs cursor-pointer ${checked ? "bg-amber-500/15 border-amber-500/40 text-amber-200" : "bg-slate-800/50 border-slate-700 text-slate-400 hover:text-slate-100"}`}>
                                                <input type="checkbox" checked={checked}
                                                    onChange={e=>{
                                                        const cur = new Set(form.macchine_compatibili || []);
                                                        if (e.target.checked) cur.add(i.id); else cur.delete(i.id);
                                                        setForm({...form, macchine_compatibili: Array.from(cur)});
                                                    }} />
                                                <span className="font-mono">{i.codice}</span>
                                                <span>{i.descrizione}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs text-slate-400">Note articolo <span className="text-slate-600">(tecniche, visibili all'operatore)</span></Label>
                            <Textarea data-testid="input-note-articolo" value={form.note_articolo}
                                      onChange={e=>setForm({...form, note_articolo: e.target.value})}
                                      placeholder="Note tecniche sull'articolo, es. 'attenzione al centraggio piste'…"
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} />
                        </div>

                        <div>
                            <Label className="text-xs text-slate-400">Note (interne ufficio)</Label>
                            <Textarea data-testid="input-note" value={form.note}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} />
                        </div>
                    </div>

                    <DialogFooter>
                        {errors.length > 0 && (
                            <div className="w-full text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2 mb-2">
                                <div className="font-bold mb-1">Non posso salvare — mancano:</div>
                                <ul className="list-disc pl-5 space-y-0.5">{errors.slice(0,4).map((e,i)=><li key={i}>{e}</li>)}</ul>
                                {errors.length > 4 && <div className="mt-1 opacity-70">…e altri {errors.length-4}</div>}
                            </div>
                        )}
                        <div className="flex gap-2 justify-end w-full">
                            <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                            <Button onClick={save} data-testid="button-save-article" disabled={!canSave}
                                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 disabled:opacity-40">Salva</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Storico dialog */}
            <Dialog open={histOpen} onOpenChange={setHistOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <History size={18} className="text-amber-400" /> Storico articolo
                            {hist && <span className="doc-code text-sm ml-2">{hist.articolo?.codice}</span>}
                        </DialogTitle>
                    </DialogHeader>
                    {!hist && <div className="py-8 text-center text-slate-500 text-xs">Caricamento…</div>}
                    {hist && (
                        <div className="max-h-[60vh] overflow-y-auto space-y-2" data-testid="timeline-storico">
                            {hist.events.length === 0 && <div className="py-8 text-center text-slate-500 text-sm">Nessun evento</div>}
                            {hist.events.map((e, i) => {
                                const meta = EVENT_META[e.tipo] || { label: e.tipo, icon: History, tone: "amber" };
                                const Ic = meta.icon;
                                return (
                                    <div key={i} className="flex gap-3 p-3 border border-slate-800 rounded-lg bg-slate-950/40" data-testid={`event-${e.numero}`}>
                                        <div className={`inline-flex p-2 rounded-lg border h-fit ${TONES[meta.tone]}`}><Ic size={14} /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <div className="flex items-center gap-2">
                                                    <Badge className={`badge-pill ${TONES[meta.tone]}`}>{meta.label}</Badge>
                                                    <span className="doc-code text-xs">{e.numero}</span>
                                                    {e.alternativa && <Badge className="badge-pill bg-violet-500/15 text-violet-300 border-violet-500/40">ALT</Badge>}
                                                </div>
                                                <div className="text-[10px] text-slate-500 font-mono">{new Date(e.data).toLocaleString("it-IT")}</div>
                                            </div>
                                            <div className="mt-1 text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-0.5">
                                                <span>Cartoni: <span className="font-mono text-slate-100 font-bold">{e.cartoni}</span></span>
                                                {e.ordine_numero && <span>Ordine: <span className="doc-code text-[10px]">{e.ordine_numero}</span></span>}
                                                {e.operatore && <span>Op: <span className="text-slate-300">{e.operatore}</span></span>}
                                                {e.ubicazione && <span>Ubic: <span className="font-mono text-slate-300">{e.ubicazione}</span></span>}
                                                {e.lotto_numero && <span>Lotto: <span className="doc-code text-[10px]">{e.lotto_numero}</span></span>}
                                                {e.pedana_numero && <span>Pedana: <span className="doc-code text-[10px]">{e.pedana_numero}</span></span>}
                                                {e.destinatario && <span>→ {e.destinatario}</span>}
                                            </div>
                                            {e.note && <div className="mt-1 text-[11px] text-slate-500 italic">"{e.note}"</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Import Excel dialog */}
            <Dialog open={importOpen} onOpenChange={(v)=>{ if (!v) { setImportOpen(false); setImportFile(null); setImportPreview(null); } }}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dialog-import">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <FileSpreadsheet size={18} className="text-emerald-400" /> Importa articoli da Excel
                        </DialogTitle>
                    </DialogHeader>
                    {importLoading && <div className="py-10 text-center text-slate-500 text-xs">Analisi in corso…</div>}
                    {!importLoading && importPreview && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2 text-xs">
                                <Badge className="badge-pill bg-slate-800 text-slate-300 border-slate-700">Totale: <b className="ml-1 font-mono">{importPreview.summary.total}</b></Badge>
                                <Badge className="badge-pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40">OK: <b className="ml-1 font-mono">{importPreview.summary.ok}</b></Badge>
                                <Badge className="badge-pill bg-amber-500/15 text-amber-300 border-amber-500/40">Duplicati: <b className="ml-1 font-mono">{importPreview.summary.duplicates}</b></Badge>
                                <Badge className="badge-pill bg-red-500/15 text-red-300 border-red-500/40">Errori: <b className="ml-1 font-mono">{importPreview.summary.errors}</b></Badge>
                            </div>
                            {(importPreview.new_clients || []).length > 0 && (
                                <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="panel-nuovi-clienti">
                                    <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold mb-2">Nuovi clienti da creare — assegna il codice breve</div>
                                    <div className="space-y-2">
                                        {importPreview.new_clients.map(c => {
                                            const key = c.nome.toLowerCase();
                                            return (
                                                <div key={key} className="flex flex-wrap items-center gap-2 text-xs">
                                                    <span className="min-w-[180px] font-semibold text-slate-100">{c.nome}</span>
                                                    <Input value={clientCodeOverrides[key] || ""}
                                                           onChange={e=>setClientCodeOverrides(p=>({...p, [key]: e.target.value.toUpperCase().slice(0,8)}))}
                                                           data-testid={`input-codice-cliente-${key}`}
                                                           className="bg-slate-950/50 border-slate-800 max-w-[120px] font-mono uppercase" />
                                                    <span className="text-[10px] text-slate-500">
                                                        {c.source === "excel" ? "da codice_articolo" : "proposto automaticamente"} · righe {c.row_nums.join(", ")}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {(importPreview.new_colors || []).length > 0 && (
                                <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-3">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Nuovi colori (auto)</div>
                                    <div className="text-xs text-slate-300 space-y-1">
                                        {importPreview.new_colors.map(c => (
                                            <div key={c.nome.toLowerCase()}>
                                                <b>{c.nome}</b> → codice <span className="font-mono text-amber-300">{c.proposed_code}</span>
                                                <span className="text-[10px] text-slate-500 ml-2">
                                                    {c.source === "excel" ? "da codice_articolo" : "auto"} · righe {c.row_nums.join(", ")}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="overflow-x-auto border border-slate-800 rounded-lg">
                                <table className="w-full text-xs" data-testid="table-import-preview">
                                    <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-950 border-b border-slate-800">
                                        <tr>
                                            <th className="text-left px-3 py-2">Riga</th>
                                            <th className="text-left px-3 py-2">Cliente · Colore</th>
                                            <th className="text-left px-3 py-2">Misure</th>
                                            <th className="text-left px-3 py-2">Gruppo misura</th>
                                            <th className="text-left px-3 py-2">Gruppo confez.</th>
                                            <th className="text-left px-3 py-2">Imballo</th>
                                            <th className="text-left px-3 py-2">Codice</th>
                                            <th className="text-left px-3 py-2">Esito</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importPreview.rows.map(r => {
                                            const asgKey = String(r.row_num);
                                            const currentAsg = misuraAssignments[asgKey] ?? (r.gruppo_is_new ? "__new__" : (r.gruppo_codice || ""));
                                            const alts = r.gruppo_alternatives || [];
                                            const showSelect = r.status !== "error" && (r.gruppo_is_new || alts.length > 0);
                                        return (
                                            <tr key={r.row_num} className={`border-b border-slate-800/60 ${r.cliente_da_creare ? "bg-amber-500/5" : ""} ${r.gruppo_is_new ? "bg-amber-500/10" : ""}`} data-testid={`import-row-${r.row_num}`}>
                                                <td className="px-3 py-2 font-mono text-slate-500">{r.row_num}</td>
                                                <td className="px-3 py-2">
                                                    {r.cliente_nome}
                                                    {r.cliente_da_creare && <Badge className="ml-1 badge-pill bg-amber-500/20 text-amber-300 border-amber-500/50 text-[9px]">DA CREARE</Badge>}
                                                    {" · "}{r.colore_nome}
                                                    {r.colore_da_creare && <Badge className="ml-1 badge-pill bg-slate-700 text-slate-300 border-slate-600 text-[9px]">nuovo</Badge>}
                                                </td>
                                                <td className="px-3 py-2 font-mono text-slate-400">
                                                    {r.larghezza_principale}{r.larghezza_soffietto ? `+${r.larghezza_soffietto}` : ""}×{r.lunghezza} · {r.spessore_my}μ · {r.rotoli_per_cartone}×{r.buste_per_rotolo}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {r.status === "error" ? <span className="text-slate-600">—</span> : (
                                                    <div className="space-y-1">
                                                        {r.gruppo_is_exact && r.gruppo_codice && (
                                                            <div className="text-[10px]"><Badge className="badge-pill bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[9px]">Gruppo {r.gruppo_codice}</Badge>
                                                            <span className="text-slate-500 ml-1 font-mono">mis. {r.descrizione_larghezza}×{r.descrizione_lunghezza}</span></div>
                                                        )}
                                                        {!r.gruppo_is_exact && !r.gruppo_is_new && r.gruppo_codice && (
                                                            <div className="text-[10px]"><Badge className={`badge-pill text-[9px] ${r.gruppo_fascetta_priority ? "bg-sky-500/20 text-sky-300 border-sky-500/40" : "bg-amber-500/20 text-amber-300 border-amber-500/40"}`}>→ Gruppo {r.gruppo_codice}{r.gruppo_fascetta_priority ? " · fascetta" : ""}</Badge>
                                                            <span className="text-slate-500 ml-1 font-mono">mis. {r.descrizione_larghezza}×{r.descrizione_lunghezza}</span></div>
                                                        )}
                                                        {r.gruppo_is_new && (
                                                            <div className="text-[10px]"><Badge className="badge-pill bg-amber-600/30 text-amber-200 border-amber-600/50 text-[9px]">NUOVO GRUPPO</Badge>
                                                            <span className="text-slate-500 ml-1 font-mono">mis. {r.descrizione_larghezza}×{r.descrizione_lunghezza}</span></div>
                                                        )}
                                                        {showSelect && (
                                                            <select value={currentAsg}
                                                                    onChange={e=>setMisuraAssignments(p=>({...p, [asgKey]: e.target.value}))}
                                                                    data-testid={`select-gruppo-${r.row_num}`}
                                                                    className="w-full max-w-[240px] bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-200 font-mono">
                                                                {r.gruppo_codice && <option value={r.gruppo_codice}>Gruppo {r.gruppo_codice} · pref. {r.gruppo_preferita_larghezza}×{r.gruppo_preferita_lunghezza}</option>}
                                                                {alts.filter(a => a.codice_breve !== r.gruppo_codice).map(a => (
                                                                    <option key={a.codice_breve} value={a.codice_breve}>Gruppo {a.codice_breve} · pref. {a.preferita_larghezza}×{a.preferita_lunghezza}</option>
                                                                ))}
                                                                <option value="__new__">— Crea nuovo gruppo —</option>
                                                            </select>
                                                        )}
                                                    </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {r.status === "error" ? <span className="text-slate-600">—</span> : (
                                                    <div className="space-y-1">
                                                        {r.conf_gruppo_is_exact && r.conf_gruppo_codice && (
                                                            <div className="text-[10px]"><Badge className="badge-pill bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[9px]">Gruppo {r.conf_gruppo_codice}</Badge>
                                                            <span className="text-slate-500 ml-1 font-mono">{r.rotoli_per_cartone}×{r.buste_per_rotolo}</span></div>
                                                        )}
                                                        {r.conf_gruppo_source === "fascetta" && r.conf_gruppo_codice && (
                                                            <div className="text-[10px]"><Badge className="badge-pill bg-sky-500/20 text-sky-300 border-sky-500/40 text-[9px]">Gruppo {r.conf_gruppo_codice} · fascetta</Badge></div>
                                                        )}
                                                        {r.conf_gruppo_is_new && (
                                                            <div className="text-[10px]"><Badge className="badge-pill bg-amber-600/30 text-amber-200 border-amber-600/50 text-[9px]">NUOVO GRUPPO</Badge></div>
                                                        )}
                                                        {(r.conf_gruppo_is_new || (r.conf_gruppo_codice && !r.conf_gruppo_is_exact)) && (
                                                            <select value={confAssignments[String(r.row_num)] ?? (r.conf_gruppo_is_new ? "__new__" : (r.conf_gruppo_codice || ""))}
                                                                    onChange={e=>setConfAssignments(p=>({...p, [String(r.row_num)]: e.target.value}))}
                                                                    data-testid={`select-conf-${r.row_num}`}
                                                                    className="w-full max-w-[240px] bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-200 font-mono">
                                                                {r.conf_gruppo_codice && <option value={r.conf_gruppo_codice}>Gruppo {r.conf_gruppo_codice}{r.conf_preferita_buste ? ` · pref. ${r.conf_preferita_rotoli}×${r.conf_preferita_buste}` : ""}</option>}
                                                                <option value="__new__">— Crea nuovo gruppo —</option>
                                                            </select>
                                                        )}
                                                    </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2">{r.imballo}</td>
                                                <td className="px-3 py-2 font-mono text-amber-300">
                                                    {r.codice_suggerito || "—"}
                                                    {r.codice_source === "excel" && (
                                                        <div className="text-[9px] text-sky-300 mt-0.5 font-normal" title={`Codice preso dall'Excel (storico). Auto sarebbe: ${r.codice_auto || "—"}`}>
                                                            da Excel (storico)
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {r.status === "ok" && <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 size={12} /> OK</span>}
                                                    {r.status === "duplicate" && <span className="inline-flex items-center gap-1 text-amber-300" title={`Esiste già: ${r.duplicate_of}`}><AlertTriangle size={12} /> Duplicato ({r.duplicate_of})</span>}
                                                    {r.status === "error" && <span className="inline-flex items-center gap-1 text-red-300" title={(r.errors||[]).join("; ")}><XCircle size={12} /> {(r.errors||[]).join("; ")}</span>}
                                                    {(r.warnings||[]).length > 0 && (
                                                        <div className="mt-1 text-[10px] text-amber-300/90 inline-flex items-center gap-1" title={r.warnings.join("; ")} data-testid={`import-warn-${r.row_num}`}>
                                                            <AlertTriangle size={10} /> {r.warnings.join(" · ")}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );})}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>{ setImportOpen(false); setImportFile(null); setImportPreview(null); }}
                                className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={doImportCommit}
                                disabled={!importPreview || importCommitting || (importPreview?.summary.ok === 0)}
                                data-testid="button-import-commit"
                                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 gap-2">
                            <Upload size={14} /> {importCommitting ? "Importazione…" : `Conferma importazione (${importPreview?.summary.ok || 0} articoli)`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
